import React, { useState, useEffect } from 'react';
import { 
  Settings, Users, User, UserCog, BarChart3, Calendar, FileText, Shield, 
  LogOut, Plus, Edit, Eye, EyeOff, Trash2, Search, Filter, Download,
  CheckCircle, XCircle, TrendingUp, Mail, Copy, MessageCircle,
  Star, Heart, Sparkles, Crown, Target, Send, X, Save, Plug
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import DashboardView from '../components/DashboardView';
import UsersTab from '../components/admin/UsersTab';
import TeamTab from '../components/admin/TeamTab';
import AIControlsTab from '../components/admin/AIControlsTab';
import IntegrationsTab from '../components/admin/IntegrationsTab';
import SecurityTab from '../components/admin/SecurityTab';

const AdminPanelNew = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [userManagementTab, setUserManagementTab] = useState('users');
  const [adminUser, setAdminUser] = useState(null);
  const [adminToken, setAdminToken] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Data states
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  
  // Cleanup states
  const [cleanupCriteria, setCleanupCriteria] = useState({
    email_patterns: [],
    name_patterns: [],
    before_date: '',
    user_ids: []
  });
  const [previewUsers, setPreviewUsers] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [emailPatternInput, setEmailPatternInput] = useState('');
  const [namePatternInput, setNamePatternInput] = useState('');
  
  // Community states
  const [communityChannels, setCommunityChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '' });
  const [adminDisplayName, setAdminDisplayName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [profileImageError, setProfileImageError] = useState(false);
  
  // Login form
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });
  
  // OTP form
  const [requiresOTP, setRequiresOTP] = useState(false);
  const [sessionToken, setSessionToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpExpiry, setOtpExpiry] = useState(null);

  const { toast } = useToast();
  const API_BASE_URL = '/api';

  // Check for existing admin session
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const user = localStorage.getItem('admin_user');
    const savedName = localStorage.getItem('admin_display_name');
    const userToken = localStorage.getItem('auth_token');
    
    if (token && user) {
      setAdminToken(token);
      setAdminUser(JSON.parse(user));
      setIsLoggedIn(true);
      setAdminDisplayName(savedName || '');
      
      // Fetch user profile data
      if (userToken) {
        fetch(`${API_BASE_URL}/auth/me`, {
          headers: { 'Authorization': `Bearer ${userToken}` }
        })
        .then(res => {
          if (res.ok) {
            return res.json();
          }
          throw new Error('Failed to fetch profile');
        })
        .then(data => {
          console.log('✅ User profile loaded:', data);
          setUserProfile(data);
        })
        .catch(err => {
          console.error('❌ Error fetching user profile:', err);
          // If user profile fails, try to get from localStorage
          const storedUser = localStorage.getItem('user_data');
          if (storedUser) {
            try {
              setUserProfile(JSON.parse(storedUser));
            } catch (e) {
              console.error('Error parsing stored user:', e);
            }
          }
        });
      }
    }
  }, []);

  // Reset profile image error when userProfile changes
  useEffect(() => {
    setProfileImageError(false);
  }, [userProfile?.profile_pic]);

  const fetchEmailTemplates = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/email-templates`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmailTemplates(data.templates || {});
      }
    } catch (error) {
      console.error('Error fetching email templates:', error);
    }
  };

  const openTemplateEditor = async (templateId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/email-templates/${templateId}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (response.ok) {
        const template = await response.json();
        setSelectedTemplate(template);
        setShowTemplateEditor(true);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load template for editing",
        variant: "destructive",
      });
    }
  };

  const saveTemplate = async (templateData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/email-templates/${selectedTemplate.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(templateData),
      });

      if (response.ok) {
        toast({
          title: "✨ Template Updated!",
          description: "Email template saved successfully",
          duration: 3000,
        });
        setShowTemplateEditor(false);
        fetchEmailTemplates();
      }
    } catch (error) {
      toast({
        title: "Save Failed",
        description: "Failed to save template changes",
        variant: "destructive",
      });
    }
  };

  const testWebhook = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/webhooks/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ webhook_url: webhookUrl }),
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: result.success ? "🪝 Webhook Test Successful!" : "Webhook Test Failed",
          description: result.message,
          duration: 4000,
        });
      }
    } catch (error) {
      toast({
        title: "Test Failed", 
        description: "Failed to test webhook",
        variant: "destructive",
      });
    }
  };

  const resetUserPassword = async (userId, userName, userEmail) => {
    if (!confirm(`Reset password for ${userName} (${userEmail})?`)) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "🔑 Password Reset Successfully",
          description: `New password sent to ${userEmail}`,
          duration: 4000,
        });
      }
    } catch (error) {
      toast({
        title: "Reset Failed",
        description: "Failed to reset user password",
        variant: "destructive",
      });
    }
  };

  const toggleUserStatus = async (userId, userName, currentStatus) => {
    if (!confirm(`${currentStatus ? 'Deactivate' : 'Activate'} ${userName}?`)) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/toggle-status`, {
        method: 'PUT', 
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "✅ Status Updated",
          description: result.message,
          duration: 3000,
        });
        fetchUsers(); // Refresh user list
      }
    } catch (error) {
      toast({
        title: "Update Failed",
        description: "Failed to update user status", 
        variant: "destructive",
      });
    }
  };

  // Load data when tab changes
  useEffect(() => {
    if (isLoggedIn) {
      // Fetch data based on active tab
      switch(activeTab) {
        case 'dashboard':
        case 'users':
        case 'analytics':
        case 'billing':
        case 'referrals':
        case 'gamification':
          fetchUsers();
          fetchAnalytics();
          fetchCommunityChannels();
          break;
        case 'community':
          fetchCommunityChannels();
          fetchCommunityPosts();
          break;
        case 'content':
        case 'conchi':
        case 'privacy':
        case 'settings':
          fetchAnalytics();
          break;
        case 'messaging':
          fetchEmailTemplates();
          break;
        default:
          break;
      }
    }
  }, [isLoggedIn, activeTab]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginForm),
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.requires_otp) {
          // OTP required - show OTP input form
          setRequiresOTP(true);
          setSessionToken(data.session_token);
          setOtpExpiry(Date.now() + (data.expires_in * 1000));
          
          toast({
            title: "🔐 OTP Sent!",
            description: "Check your email for the 6-digit code",
            duration: 5000,
          });
        } else {
          // No OTP required - login complete
          localStorage.setItem('admin_token', data.access_token);
          localStorage.setItem('admin_user', JSON.stringify(data.admin));
          setAdminToken(data.access_token);
          setAdminUser(data.admin);
          setIsLoggedIn(true);
          
          toast({
            title: "🌟 Welcome to Constant Love™ Admin",
            description: "Successfully logged into your empowerment platform",
            duration: 3000,
          });
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Invalid credentials. Please try again.');
      }
    } catch (error) {
      setError('Login failed. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/admin/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_token: sessionToken,
          otp_code: otpCode
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        console.log('🔐 Admin OTP verified, received data:', {
          hasAccessToken: !!data.access_token,
          hasUserAccessToken: !!data.user_access_token,
          hasUser: !!data.user,
          admin: data.admin
        });
        
        // Store admin token and user
        localStorage.setItem('admin_token', data.access_token);
        localStorage.setItem('admin_user', JSON.stringify(data.admin));
        console.log('✅ Admin token stored');
        
        // Store user token and data for unified access to user features
        if (data.user_access_token) {
          localStorage.setItem('auth_token', data.user_access_token);
          localStorage.setItem('token', data.user_access_token);
          console.log('✅ User access token stored');
          
          // Fetch complete user data from user endpoint to ensure AuthContext has everything
          try {
            console.log('📡 Fetching user data from /api/auth/me...');
            const userResponse = await fetch(`${API_BASE_URL}/auth/me`, {
              headers: {
                'Authorization': `Bearer ${data.user_access_token}`
              }
            });
            
            if (userResponse.ok) {
              const userData = await userResponse.json();
              // Store user data so AuthContext can use it immediately
              localStorage.setItem('user_data', JSON.stringify(userData));
              console.log('✅ User data pre-loaded:', userData);
              
              // Show success message
              toast({
                title: "✅ Admin Access Granted!",
                description: "Welcome to the admin panel...",
                duration: 2000,
              });
              
              // Redirect to admin panel
              setTimeout(() => {
                window.location.href = '/app/admin';
              }, 1000);
            } else {
              const errorText = await userResponse.text();
              console.error('❌ Failed to fetch user data:', errorText);
              toast({
                title: "Warning",
                description: "Could not load user data. Trying alternate login...",
                variant: "destructive",
                duration: 3000,
              });
              // Try redirecting anyway
              setTimeout(() => {
                window.location.href = '/app';
              }, 1500);
            }
          } catch (error) {
            console.error('❌ Error fetching user data:', error);
            toast({
              title: "Warning",
              description: "Could not load user data. Redirecting...",
              variant: "destructive",
              duration: 2000,
            });
            // Try redirecting anyway
            setTimeout(() => {
              window.location.href = '/app';
            }, 1500);
          }
        } else {
          console.error('❌ No user_access_token received from backend');
          setError('Login incomplete - missing user access token. Please contact support.');
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Invalid OTP code. Please try again.');
      }
    } catch (error) {
      setError('OTP verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    // Re-trigger login to resend OTP
    setOtpCode('');
    setError('');
    await handleLogin({ preventDefault: () => {} });
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/analytics`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setAdminToken(null);
    setAdminUser(null);
    setIsLoggedIn(false);
    setActiveTab('dashboard');
    
    toast({
      title: "Logged Out 💕",
      description: "Thank you for managing the Constant Love™ movement",
      duration: 3000
    });
  };

  // Community management functions
  const fetchCommunityChannels = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/community/channels`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCommunityChannels(data.channels || []);
      }
    } catch (error) {
      console.error('Error fetching community channels:', error);
    }
  };

  const createChannel = async (name, description) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/community/channels`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, description }),
      });

      if (response.ok) {
        toast({
          title: "✨ Channel Created!",
          description: `${name} channel is ready for community posts`,
          duration: 3000,
        });
        fetchCommunityChannels();
      }
    } catch (error) {
      toast({
        title: "Creation Failed",
        description: "Failed to create channel",
        variant: "destructive",
      });
    }
  };

  // Community posts for admin moderation
  const [communityPosts, setCommunityPosts] = useState([]);
  
  const fetchCommunityPosts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/community/posts?limit=50`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCommunityPosts(data.posts || []);
      }
    } catch (error) {
      console.error('Error fetching community posts:', error);
    }
  };

  const adminDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/community/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        toast({
          title: "Post Deleted",
          description: "Post has been removed from the community",
          duration: 3000
        });
        fetchCommunityPosts();
      }
    } catch (error) {
      toast({
        title: "Delete Failed",
        description: "Failed to delete post",
        variant: "destructive"
      });
    }
  };


  const toggleChannelVisibility = async (channelId, visible) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/community/channels/${channelId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ visible }),
      });

      if (response.ok) {
        toast({
          title: visible ? "👁️ Channel Visible" : "🔒 Channel Hidden",
          description: `Channel visibility updated`,
          duration: 3000,
        });
        fetchCommunityChannels();
      }
    } catch (error) {
      toast({
        title: "Update Failed",
        description: "Failed to update channel visibility",
        variant: "destructive",
      });
    }
  };

  const deleteChannel = async (channelId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/community/channels/${channelId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
        },
      });

      if (response.ok) {
        toast({
          title: "🗑️ Channel Deleted",
          description: "Channel and all posts removed",
          duration: 3000,
        });
        fetchCommunityChannels();
      }
    } catch (error) {
      toast({
        title: "Delete Failed",
        description: "Failed to delete channel",
        variant: "destructive",
      });
    }
  };

  const createAdminPost = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/community/channels/${selectedChannel.id}/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: newPost.title,
          content: newPost.content,
        }),
      });

      if (response.ok) {
        toast({
          title: "📝 Post Published!",
          description: `Your message has been shared with the community`,
          duration: 3000,
        });
        setShowChannelModal(false);
        setSelectedChannel(null);
        setNewPost({ title: '', content: '' });
        fetchCommunityChannels();
      }
    } catch (error) {
      toast({
        title: "Post Failed",
        description: "Failed to publish post",
        variant: "destructive",
      });
    }
  };

  // Cleanup functions
  const addEmailPattern = () => {
    if (emailPatternInput && !cleanupCriteria.email_patterns.includes(emailPatternInput)) {
      setCleanupCriteria({
        ...cleanupCriteria,
        email_patterns: [...cleanupCriteria.email_patterns, emailPatternInput]
      });
      setEmailPatternInput('');
    }
  };

  const removeEmailPattern = (pattern) => {
    setCleanupCriteria({
      ...cleanupCriteria,
      email_patterns: cleanupCriteria.email_patterns.filter(p => p !== pattern)
    });
  };

  const addNamePattern = () => {
    if (namePatternInput && !cleanupCriteria.name_patterns.includes(namePatternInput)) {
      setCleanupCriteria({
        ...cleanupCriteria,
        name_patterns: [...cleanupCriteria.name_patterns, namePatternInput]
      });
      setNamePatternInput('');
    }
  };

  const removeNamePattern = (pattern) => {
    setCleanupCriteria({
      ...cleanupCriteria,
      name_patterns: cleanupCriteria.name_patterns.filter(p => p !== pattern)
    });
  };

  const handlePreviewCleanup = async () => {
    setCleanupLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api/admin/cleanup/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(cleanupCriteria)
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewUsers(data.users_to_delete || []);
        setShowPreview(true);
        
        toast({
          title: "🔍 Preview Generated",
          description: `Found ${data.total_count} users matching criteria`,
          duration: 4000
        });
      } else {
        throw new Error('Failed to preview cleanup');
      }
    } catch (error) {
      console.error('Error previewing cleanup:', error);
      toast({
        title: "❌ Preview Failed",
        description: error.message,
        variant: "destructive",
        duration: 4000
      });
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleExecuteCleanup = async () => {
    if (!window.confirm(`⚠️ WARNING: This will permanently delete ${previewUsers.length} users and ALL their related data (conversations, affirmations, garden progress, etc.). This action CANNOT be undone!\n\nAre you absolutely sure?`)) {
      return;
    }

    setCleanupLoading(true);
    try {
      const user_ids = previewUsers.map(u => u.id);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api/admin/cleanup/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          user_ids: user_ids,
          delete_related_data: true
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        toast({
          title: "✅ Cleanup Complete",
          description: `Successfully deleted ${data.deleted_count} users and their data`,
          duration: 5000
        });

        // Reset state
        setShowPreview(false);
        setPreviewUsers([]);
        setCleanupCriteria({
          email_patterns: [],
          name_patterns: [],
          before_date: '',
          user_ids: []
        });

        // Refresh users list
        fetchUsers();
      } else {
        throw new Error('Failed to execute cleanup');
      }
    } catch (error) {
      console.error('Error executing cleanup:', error);
      toast({
        title: "❌ Cleanup Failed",
        description: error.message,
        variant: "destructive",
        duration: 4000
      });
    } finally {
      setCleanupLoading(false);
    }
  };

  // Format date consistently
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 bg-white/80 backdrop-blur-sm border-rose-200 shadow-xl">
          <div className="text-center mb-8">
            <img 
              src="/constant-love-logo.png" 
              alt="Constant Love™" 
              className="w-20 h-20 mx-auto mb-4 object-contain"
            />
            <h1 className="text-2xl font-serif font-bold text-gray-900 mb-2">
              {requiresOTP ? '🔐 Enter OTP' : 'Admin Panel'}
            </h1>
            <p className="text-gray-600 font-sans">
              {requiresOTP ? 'Check your email for the verification code' : 'Manage your Constant Love™ movement'}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm font-sans">{error}</p>
            </div>
          )}

          {!requiresOTP ? (
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-sans">Username</label>
                <input
                  type="text"
                  name="username"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                  className="w-full px-3 py-2 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-sans">Password</label>
                <input
                  type="password"
                  name="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  className="w-full px-3 py-2 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-rose-500 hover:bg-rose-600 text-white py-3 font-serif"
              >
                {loading ? 'Verifying...' : '🌟 Access Admin Panel'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleOTPVerify} className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800 font-sans">
                  📧 A 6-digit code has been sent to <strong>constantlove.app@gmail.com</strong>
                </p>
                <p className="text-xs text-blue-600 font-sans mt-2">
                  Code expires in 5 minutes
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-sans">Enter 6-Digit OTP</label>
                <input
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full px-3 py-2 border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 text-center text-2xl tracking-widest font-mono"
                  maxLength={6}
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={loading || otpCode.length !== 6}
                className="w-full bg-rose-500 hover:bg-rose-600 text-white py-3 font-serif disabled:opacity-50"
              >
                {loading ? 'Verifying...' : '✅ Verify & Login'}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={loading}
                  className="text-sm text-rose-600 hover:text-rose-700 font-sans underline"
                >
                  Didn't receive code? Resend OTP
                </button>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setRequiresOTP(false);
                    setOtpCode('');
                    setError('');
                  }}
                  className="text-sm text-gray-600 hover:text-gray-700 font-sans"
                >
                  ← Back to login
                </button>
              </div>
            </form>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5' }}>
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-md border-b border-rose-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex justify-between items-center">
            {/* Logo and Title */}
            <div className="flex items-center space-x-3 md:space-x-4">
              <img 
                src="/constant-love-logo.png" 
                alt="Constant Love™" 
                className="w-12 h-12 md:w-16 md:h-16 object-contain"
              />
              <div>
                <h1 className="text-lg md:text-2xl font-serif font-bold text-gray-900">Constant Love™ Admin</h1>
                <p className="text-gray-600 font-sans text-xs md:text-sm hidden md:block">Empowerment Platform Management</p>
              </div>
            </div>
            
            {/* Profile Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-rose-50 transition-colors relative"
              >
                <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white text-sm md:text-base font-bold overflow-hidden">
                  {userProfile?.profile_pic && userProfile.profile_pic !== '' && !profileImageError ? (
                    <img 
                      src={userProfile.profile_pic} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                      onError={() => setProfileImageError(true)}
                    />
                  ) : (
                    (userProfile?.name || adminUser?.username?.split('@')[0])?.charAt(0).toUpperCase() || 'A'
                  )}
                </div>
                <div className="hidden md:block text-left">
                  <span className="text-gray-700 font-medium text-sm block">{userProfile?.name || adminUser?.username || 'Admin'}</span>
                  <span className="text-xs bg-gradient-to-r from-purple-500 to-pink-600 text-white px-2 py-0.5 rounded-full font-bold">ADMIN</span>
                </div>
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        window.location.href = '/app';
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-rose-50 flex items-center"
                    >
                      <Star className="w-4 h-4 mr-2 text-purple-500" />
                      Switch to User App
                    </button>
                    <button
                      onClick={() => {
                        window.location.href = '/app/profile';
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-rose-50 flex items-center"
                    >
                      <User className="w-4 h-4 mr-2" />
                      Profile Settings
                    </button>
                    <hr className="my-1 border-gray-200" />
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-rose-50 flex items-center"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Beautiful Navigation Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-8 overflow-hidden" style={{ borderColor: '#FADADD', borderWidth: '2px' }}>
          {/* Navigation Tabs */}
          <div className="inline-flex rounded-2xl p-1 backdrop-blur-xl"
               style={{
                 background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.5) 100%)',
                 border: '1px solid rgba(255, 182, 193, 0.2)',
               }}>
            {[
              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { id: 'users', label: 'User Management', icon: Users },
              { id: 'ai-controls', label: 'AI Controls', icon: Settings },
              { id: 'integrations', label: 'Integrations', icon: Plug },
              { id: 'security', label: 'Security & Audit', icon: Shield }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                  activeTab === id
                    ? 'bg-gradient-to-r from-rose-400 to-pink-500 text-white shadow-lg'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <DashboardView adminToken={adminToken} adminUser={adminUser} />
        )}

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Sub-tab Navigation */}
            <div className="inline-flex rounded-2xl p-1 backdrop-blur-xl"
                 style={{
                   background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.5) 100%)',
                   border: '1px solid rgba(255, 182, 193, 0.2)',
                 }}>
              <button
                onClick={() => setUserManagementTab('users')}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                  userManagementTab === 'users'
                    ? 'bg-gradient-to-r from-rose-400 to-pink-500 text-white shadow-lg'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Users className="w-5 h-5" />
                <span>Users</span>
              </button>
              <button
                onClick={() => setUserManagementTab('team')}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                  userManagementTab === 'team'
                    ? 'bg-gradient-to-r from-purple-400 to-violet-500 text-white shadow-lg'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <UserCog className="w-5 h-5" />
                <span>Team</span>
              </button>
            </div>

            {/* Render Selected Sub-tab */}
            {userManagementTab === 'users' ? <UsersTab /> : <TeamTab />}
          </div>
        )}

        {/* AI Controls Tab */}
        {activeTab === 'ai-controls' && (
          <AIControlsTab />
        )}

        {/* Integrations Tab */}
        {activeTab === 'integrations' && (
          <IntegrationsTab />
        )}

        {/* Security & Audit Tab */}
        {activeTab === 'security' && (
          <SecurityTab />
        )}

      </div>
    </div>
  );
};

export default AdminPanelNew;
